import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useFilterParams } from "hooks/useFilterParams";
import { useGetMobileTariffsQuery } from "hooks/mobileQueries";
import { MOBILE_TAGS_QUERY, PROVIDERS_V2_INFO, OPERATORS_QUERY } from "Services/queries";
import { useWithDefaultParams } from "Services/useWithDefaultParams";

export const useGetMobileTagsQuery = (skip = false) => {
	const { params } = useWithDefaultParams();

	const mobileTagsQuery = useQuery(MOBILE_TAGS_QUERY, {
		skip,
		variables: {
			region_url: params.regionUrl,
			provider_url: params.operator,
			tag_type: "mobile",
		},
		notifyOnNetworkStatusChange: true,
	});

	return {
		mobileTags: mobileTagsQuery?.data?.mobileTags?.data,
		isLoading: mobileTagsQuery.loading,
	};
};

export const useProvidersInfoQuery = () => {
	const { params } = useWithDefaultParams();

	const providersInfoQuery = useQuery(PROVIDERS_V2_INFO, {
		variables: {
			regionUrl: params.regionUrl,
		},
	});

	return {
		info: providersInfoQuery.data?.providersInfo,
		isInfoLoading: providersInfoQuery.loading,
	};
};

export const useOperatorListQuery = () => {
	const { params } = useWithDefaultParams();

	const providersListQuery = useQuery(OPERATORS_QUERY, {
		variables: {
			filter: `region.url=${params.regionUrl}&mobile_operator=1`,
			limit: 30,
			offset: 0,
		},
	});

	return {
		operatorsList: providersListQuery.data?.operators?.data,
		isOperatorsListLoading: providersListQuery.loading,
	};
};

export const useFiltersTariffs = (search, action = false) => {
	const { setFilterParams, filterParams, resetFiltersParams } = useFilterParams();

	const sort = search
		.slice(1, search.length)
		.split("&")
		.filter((el) => el.slice(0, 5) === "sort=");

	let strSort;
	if (sort.length) {
		strSort = sort[0];
	}

	const { tariffs, isTariffsLoading, fetchMoreTariffs, totalTariffs } = useGetMobileTariffsQuery({
		sort: !strSort ? "-is_exclusive" : strSort.slice(5, strSort.length),
		action,
	});

	const { params } = useWithDefaultParams();
	const { info, isInfoLoading } = useProvidersInfoQuery();

	const { operatorsList, isOperatorsListLoading } = useOperatorListQuery();

	const currentOperator = useMemo(() => {
		if (isOperatorsListLoading) {
			return null;
		}
		if (params.operator) {
			return operatorsList.find((operator) => operator.url_name === params.operator);
		}
		return operatorsList.find((operator) => operator.id === filterParams.providers);
	}, [params.operator, filterParams.providers, isOperatorsListLoading, operatorsList]);

	const [currentInitalFilters, setCurrentInitalFilters] = useState(null);

	useEffect(() => {
		if (isInfoLoading || isOperatorsListLoading) {
			return;
		}

		if (currentOperator) {
			setCurrentInitalFilters({
				gigabytes: {
					min: currentOperator.info.min_gigabytes,
					max: currentOperator.info.max_gigabytes,
				},
				minutes: {
					min: currentOperator.info.min_minutes,
					max: currentOperator.info.max_minutes,
				},
				price: {
					min: currentOperator.info.min_price,
					max: currentOperator.info.max_price,
				},
				operators: operatorsList,
			});
		} else {
			setCurrentInitalFilters({
				gigabytes: {
					min: info?.min_gigabytes,
					max: info?.max_gigabytes,
				},
				minutes: {
					min: info?.min_minutes,
					max: info?.max_minutes,
				},
				price: { min: info?.min_price, max: info?.max_price },
				operators: operatorsList,
			});
		}
	}, [isInfoLoading, info, isOperatorsListLoading, operatorsList, currentOperator]);

	return {
		tariffs,
		fetchMoreTariffs,
		totalTariffs,
		currentOperator,
		isTariffsLoading,
		resetFilters: resetFiltersParams,
		setFilters: setFilterParams,
		isLoading: isTariffsLoading || isInfoLoading || isOperatorsListLoading,
		initalFilters: currentInitalFilters,
		currentFilters: filterParams,
	};
};
